import { useEffect } from 'react';

import { cx } from '@emotion/css';
import { Col } from 'antd';
import { isUndefined } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doCreateOrder } from 'src/actions';
import { useSubmittedAccountSelector } from 'src/hooks';
import {
  MModalBody,
  MModalFooter,
  MButton,
  MFormTextField,
  MModalTitle,
  MTooltip,
  MTypography,
  CurrencyField,
} from 'src/lib';
import { mapOrderDurationDtoToLabel, mapOrderTypeDtoToLabel } from 'src/mappers';
import { SecurityOrderView, Quote } from 'src/models';
import { BaseStyle, AlignItems, JustifyContent, Display, Spacing, ColorConstant } from 'src/styles';

interface TradeSecurityHoldingReviewStepProps {
  quote: Quote;
  quantity?: number;
  order: SecurityOrderView;
  onClose: () => void;
  onBack: () => void;
  onServiceFee: () => void;
  onLastPrice: () => void;
  onReadMore: () => void;
  onSuccess?: () => void;
  onError?: () => void;
}

export const SecurityHoldingTradeReviewStep = ({
  quote,
  quantity,
  order,
  onBack,
  onClose,
  onServiceFee,
  onLastPrice,
  onReadMore,
  onSuccess,
  onError,
}: TradeSecurityHoldingReviewStepProps) => {
  const dispatch = useDispatch();
  const { account } = useSubmittedAccountSelector();

  const isSuccess = useSelector((state: any) => Boolean(state.orders.create.__succeeded));
  const isLoading = useSelector((state: any) => Boolean(state.orders.create.isLoading));
  const isError = useSelector((state: any) => Boolean(state.orders.create.__failed));

  const onSubmit = () => {
    dispatch(
      doCreateOrder({
        params: {
          accountId: account.accountId,
        },
        body: {
          action: order.action,
          duration: order.duration,
          orderType: order.type,
          price: order.price,
          quantity: order.quantity,
          stop: order.stop,
          symbol: quote.symbol,
        },
      }),
    );
  };

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      onError?.();
    }
  }, [isError]);

  return (
    <>
      <MModalTitle>Review Security {order.action} Order</MModalTitle>
      <MModalBody>
        <Col span={24} className={Spacing.my12}>
          <MTypography variant='h2' align='center' color={ColorConstant.BRAND6} weight='semibold'>
            Review Order
          </MTypography>
        </Col>
        <Col span={24} className={Spacing.my24}>
          <MTypography variant='subtitle2' align='center'>
            If you experience difficulties, please contact{' '}
            <a href={`mailto:clientservices@myipo.com`}>client services</a>.
            <br />
            Execution price, speed and liquidity are affected by many factors, including market volatility, size, price,
            low volume, type of order and the exchange the shares are traded on. <a onClick={onReadMore}>Read more</a>.
          </MTypography>
        </Col>
        <Col span={24}>
          <MFormTextField label='Security' value={`${quote.description} (${quote.symbol})`} />
        </Col>
        <Col span={24}>
          <MFormTextField label='Order Type' value={order.type ? mapOrderTypeDtoToLabel(order.type) : undefined} />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Last Price
                <MTooltip showIcon onClick={() => onLastPrice()} />
              </>
            }
            value={<CurrencyField value={quote.last} />}
          />
        </Col>
        {!isUndefined(quantity) && (
          <Col span={24}>
            <MFormTextField label='Shares Held' value={quantity} />
          </Col>
        )}
        {order.price ? (
          <Col span={24}>
            <MFormTextField label='Limit Price' value={<CurrencyField value={order.price} />} />
          </Col>
        ) : null}
        {order.stop ? (
          <Col span={24}>
            <MFormTextField label='Stop Price' value={<CurrencyField value={order.stop} />} />
          </Col>
        ) : null}
        <Col span={24}>
          <MFormTextField label={`Shares to ${order.action}`} value={order.quantity} />
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Order Expiration'
            value={order.duration ? mapOrderDurationDtoToLabel(order.duration) : undefined}
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Service Fee
                <MTooltip showIcon onClick={() => onServiceFee()} />
              </>
            }
            value='$10.00'
          />
        </Col>
        <Col span={24}>
          <MFormTextField label='Estimated Total' value={<CurrencyField value={order.estimatedTotal} />} />
        </Col>
      </MModalBody>
      <MModalFooter>
        <Col span={24} className={cx(Display.flex, AlignItems.center, JustifyContent.spaceBetween)}>
          <MButton type='tertiary' onClick={onClose} disabled={isLoading}>
            Close
          </MButton>
          <Col className={BaseStyle.levelRight}>
            <MButton type='secondary' onClick={onBack} disabled={isLoading}>
              Back
            </MButton>
            <MButton onClick={onSubmit} loading={isLoading}>
              Submit
            </MButton>
          </Col>
        </Col>
      </MModalFooter>
    </>
  );
};
